import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Pause as PauseIcon,
  ViewStream as ViewStreamIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/PageHeader';
import { elevatedPanelSx } from '../../constants/shape';
import StatusChip from '../../components/StatusChip';

const SortingLines: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [selectedLine, setSelectedLine] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    status: 'Active',
    capacity: '',
    currentLoad: '',
    efficiency: '',
    operator: '',
    lastMaintenance: '',
    notes: '',
  });

  // Mock data for demonstration
  const sortingLines = [
    {
      id: '1',
      name: 'Line A',
      type: 'Plastic',
      status: 'Active',
      capacity: '2 tons/hour',
      currentLoad: '75%',
      efficiency: '92%',
      operator: 'John Smith',
      lastMaintenance: '2024-03-15',
      currentMaterial: 'PET Bottles',
      processedToday: '12.5 tons',
    },
    // Add more mock lines as needed
  ];

  const lineTypes = [
    'Plastic',
    'Paper',
    'Metal',
    'Glass',
    'Mixed',
  ];

  const handleOpen = (line?: any) => {
    if (line) {
      setSelectedLine(line);
      setFormData({
        name: line.name,
        type: line.type,
        status: line.status,
        capacity: line.capacity,
        currentLoad: line.currentLoad,
        efficiency: line.efficiency,
        operator: line.operator,
        lastMaintenance: line.lastMaintenance,
        notes: '',
      });
    } else {
      setSelectedLine(null);
      setFormData({
        name: '',
        type: '',
        status: 'Active',
        capacity: '',
        currentLoad: '',
        efficiency: '',
        operator: '',
        lastMaintenance: '',
        notes: '',
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedLine(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    handleClose();
  };

  return (
    <Box>
      <PageHeader
        icon={<ViewStreamIcon />}
        title={t('recyclingPages.sortingLines.title')}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
            sx={{
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
              borderRadius: '12px',
              px: 3,
              py: 1.5,
            }}
          >
            {t('recyclingPages.sortingLines.addLine')}
          </Button>
        }
      />

      <Paper elevation={2} sx={elevatedPanelSx}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t('common.lineName')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('common.type')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('common.status')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('common.currentLoad')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('common.efficiency')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('recyclingPages.sortingLines.currentMaterial')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('recyclingPages.sortingLines.processedToday')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('common.operator')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortingLines.map((line) => (
                <TableRow 
                  key={line.id}
                  sx={{ 
                    '&:hover': { 
                      bgcolor: theme.palette.action.hover,
                    },
                    transition: 'all 0.3s ease-in-out',
                  }}
                >
                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {line.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('recyclingPages.sortingLines.capacityLabel', { value: line.capacity })}
                    </Typography>
                  </TableCell>
                  <TableCell>{line.type}</TableCell>
                  <TableCell>
                    <StatusChip label={line.status} value={line.status} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={parseInt(line.currentLoad)} 
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: theme.palette.grey[200],
                          '& .MuiLinearProgress-bar': {
                            bgcolor: theme.palette.primary.main,
                          },
                        }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {line.currentLoad}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {line.efficiency}
                    </Typography>
                  </TableCell>
                  <TableCell>{line.currentMaterial}</TableCell>
                  <TableCell>{line.processedToday}</TableCell>
                  <TableCell>{line.operator}</TableCell>
                  <TableCell align="right">
                    <IconButton 
                      sx={{ 
                        color: theme.palette.success.main,
                        '&:hover': { 
                          bgcolor: theme.palette.success.light,
                          color: theme.palette.success.contrastText,
                        },
                      }}
                    >
                      <PlayIcon />
                    </IconButton>
                    <IconButton 
                      sx={{ 
                        color: theme.palette.warning.main,
                        '&:hover': { 
                          bgcolor: theme.palette.warning.light,
                          color: theme.palette.warning.contrastText,
                        },
                      }}
                    >
                      <PauseIcon />
                    </IconButton>
                    <IconButton 
                      sx={{ 
                        color: theme.palette.error.main,
                        '&:hover': { 
                          bgcolor: theme.palette.error.light,
                          color: theme.palette.error.contrastText,
                        },
                      }}
                    >
                      <StopIcon />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleOpen(line)}
                      sx={{ 
                        color: theme.palette.primary.main,
                        '&:hover': { 
                          bgcolor: theme.palette.primary.light,
                          color: theme.palette.primary.contrastText,
                        },
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      sx={{ 
                        color: theme.palette.error.main,
                        '&:hover': { 
                          bgcolor: theme.palette.error.light,
                          color: theme.palette.error.contrastText,
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          borderRadius: "16px 16px 0 0",
        }}>
          {selectedLine ? t('recyclingPages.sortingLines.editDialogTitle') : t('recyclingPages.sortingLines.addDialogTitle')}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('common.lineName')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>{t('common.type')}</InputLabel>
                  <Select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    label={t('common.type')}
                    sx={{
                      borderRadius: '8px',
                    }}
                  >
                    {lineTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>{t('common.status')}</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    label={t('common.status')}
                    sx={{
                      borderRadius: '8px',
                    }}
                  >
                    <MenuItem value="Active">{t('common.active')}</MenuItem>
                    <MenuItem value="Maintenance">{t('containers.status.maintenance')}</MenuItem>
                    <MenuItem value="Inactive">{t('common.inactive')}</MenuItem>
                    <MenuItem value="Error">{t('common.unknown')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('common.capacity')}
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                  required
                  fullWidth
                  placeholder={t('recyclingPages.sortingLines.capacityPlaceholder')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('common.currentLoad')}
                  value={formData.currentLoad}
                  onChange={(e) => setFormData({ ...formData, currentLoad: e.target.value })}
                  required
                  fullWidth
                  placeholder={t('recyclingPages.sortingLines.currentLoadPlaceholder')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('common.efficiency')}
                  value={formData.efficiency}
                  onChange={(e) => setFormData({ ...formData, efficiency: e.target.value })}
                  required
                  fullWidth
                  placeholder={t('recyclingPages.sortingLines.efficiencyPlaceholder')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('common.operator')}
                  value={formData.operator}
                  onChange={(e) => setFormData({ ...formData, operator: e.target.value })}
                  required
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('common.lastMaintenance')}
                  type="date"
                  value={formData.lastMaintenance}
                  onChange={(e) => setFormData({ ...formData, lastMaintenance: e.target.value })}
                  required
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('common.notes')}
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={handleClose}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.text.primary,
                },
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                backgroundColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
                borderRadius: '8px',
                px: 3,
              }}
            >
              {selectedLine ? t('common.update') : t('common.add')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default SortingLines; 